import {Action, Card, EmptyStatus, openConfirmDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useDismissInterestVehicleRecommendationsMutation,
  useGetInterestVehicleRecommendationsQuery,
} from '@dms/api/finance';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {buildArray, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useInterestData} from '../../hooks/useInterestData';
import {RecommendedVehicles} from './components/RecommendedVehicles';

interface VehicleRecommendationsProps extends RequiredTestIdProps {
  interestId: string;
}

export function VehicleRecommendations(props: VehicleRecommendationsProps) {
  const {data: recommendations} = useGetInterestVehicleRecommendationsQuery({
    interestId: props.interestId,
  });

  const {interest} = useInterestData(props.interestId);
  const {data: interestParticipation} = useGetParticipationQuery({
    recordId: props.interestId,
    resourceId: EntityResourceIds.interest,
  });

  const [canDismissRecommendations] = usePermissions({
    permissionKeys: ['interestDismissVehicleRecommendations'],
    scopes: {
      interestDismissVehicleRecommendations: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
    },
  });

  const [dismissRecommendations, {isLoading: isDismissLoading}] =
    useDismissInterestVehicleRecommendationsMutation();

  // The card is optional, so it stays hidden while loading and when the request fails
  if (isNil(recommendations) || recommendations.status === 'HIDDEN') {
    return null;
  }

  const handleDismiss = () =>
    openConfirmDialog({
      text: i18n.t('entity.interest.vehicleRecommendations.dismissConfirm'),
      onConfirm: () =>
        dismissRecommendations({interestId: props.interestId}).unwrap().catch(handleApiError),
      'data-testid': suffixTestId('dismissDialog', props),
    });

  return (
    <Card
      title={i18n.t('entity.interest.vehicleRecommendations.title')}
      titleIcon="action/ai"
      actions={buildArray<Action>().when(canDismissRecommendations, {
        type: 'button',
        variant: 'link',
        leftIcon: 'navigation/close',
        title: i18n.t('general.labels.dismiss'),
        isLoading: isDismissLoading,
        onClick: handleDismiss,
        'data-testid': suffixTestId('dismiss', props),
      })}
      data-testid={props['data-testid']}
    >
      {match(recommendations.status)
        .with('AVAILABLE', () => (
          <RecommendedVehicles
            interestId={props.interestId}
            recommendations={recommendations.vehicles}
            data-testid={suffixTestId('vehicles', props)}
          />
        ))
        .with('NO_MATCH', () => (
          <EmptyStatus
            variant="vehicle"
            headline={i18n.t('entity.vehicle.labels.noMatchingVehicles')}
            data-testid={suffixTestId('noMatch', props)}
          />
        ))
        .exhaustive()}
    </Card>
  );
}
